import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { 
  Layers, 
  Target, 
  Zap, 
  CheckCircle2, 
  ArrowRight,
  BarChart3,
  FileText,
  MessageSquare,
  LayoutDashboard
} from 'lucide-react';

const features = [
  {
    icon: Target,
    title: 'AI Execution Plans',
    description: 'Transform project scope into structured milestones, tasks, and blockers with a single click.'
  },
  {
    icon: CheckCircle2,
    title: 'Categorized Checklists',
    description: 'Tasks organized by team—Engineering, Business, Vendor, Design—for clear ownership.'
  },
  {
    icon: MessageSquare,
    title: 'Natural Language Updates',
    description: 'Update your plan with simple prompts like "Mark API integration complete."'
  },
  {
    icon: FileText,
    title: 'Executive Briefs',
    description: 'Generate concise 150-word summaries for stakeholder communication.'
  },
  {
    icon: BarChart3,
    title: 'Confidence Scoring',
    description: 'AI-assessed project health score based on scope clarity and risk factors.'
  },
  {
    icon: Zap,
    title: 'No Admin Overhead',
    description: 'Focus on execution, not tool configuration. Pure signal, zero noise.'
  }
];

export const LandingPage = () => {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full mb-6">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              <span className="text-sm font-medium text-muted-foreground">AI-Powered Project Execution</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              Your Daily<br />
              <span className="text-primary">Execution Copilot</span>
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-lg">
              Transform project requirements into actionable execution plans. Track milestones, 
              manage blockers, and generate executive briefs—all powered by AI.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button size="lg" className="w-full sm:w-auto" data-testid="hero-go-to-dashboard">
                    Go to Dashboard
                    <LayoutDashboard className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/signup">
                    <Button size="lg" className="w-full sm:w-auto" data-testid="hero-get-started">
                      Start Free
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="hero-sign-in">
                      Sign In
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          <div className="animate-fade-in stagger-2 hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl transform rotate-3"></div>
              <img 
                src="https://images.unsplash.com/photo-1758691736424-4b4273948341?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpb24lMjBwcm9kdWN0JTIwbWFuYWdlbWVudHxlbnwwfHx8fDE3NzA5ODk5Nzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Product Team Collaboration"
                className="relative rounded-2xl border border-slate-200 shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground mb-4">
              Everything You Need to Execute
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              No Jira. No admin overhead. Just intelligent project execution that adapts to your needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:border-primary/30 transition-colors animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary rounded-2xl p-8 md:p-12 text-center animate-fade-in">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl mb-6">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Execute Smarter?
            </h2>
            <p className="text-primary-foreground/80 mb-8 max-w-lg mx-auto">
              Join product managers who've replaced spreadsheets and complex tools with AI-powered execution.
            </p>
            {user ? (
              <Link to="/dashboard">
                <Button size="lg" variant="secondary" data-testid="cta-go-to-dashboard">
                  Go to Dashboard
                  <LayoutDashboard className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to="/signup">
                <Button size="lg" variant="secondary" data-testid="cta-get-started">
                  Get Started Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                <Layers className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-semibold text-foreground">NexusAI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 NexusAI. Built for Product Managers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
